'use client';

/*
 * SHARED LEGACY THEME PRESENTATION (moved here from components/Header in Phase 8, code unchanged).
 * Marble.Theme.01 and Marble.Theme.02 render this header identically, so it is one shared primitive rather than two
 * copies. It is NOT Storefront Core: it is presentation that a future theme is free to replace.
 * Known debt, deliberately not fixed here (the visual baseline freezes it): the top bar hard-codes one contact address
 * for every tenant.
 *
 * The category mega-menu no longer fetches anything: the content of every root (its subcategories, or its products when it has
 * none) is resolved by Storefront Core with the page (lib/category-menu-previews.ts) and arrives as `categoryPreviews`, so the menu
 * opens and switches roots with its content already there — there is no loading state to render.
 */
import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { usePathname, useSearchParams } from 'next/navigation';
import { usePageLocaleContext } from '@/app/context/PageLocaleContext';
import { useCustomerAuthContext } from '@/app/context/CustomerAuthContext';
import type { CategoryMenuPreviews } from '@/lib/category-menu-previews';
import { createTranslator } from '@/lib/localization';
import { menuItemHref } from '@/lib/storefront-links';
import { buildLanguageHref as buildLanguageHrefFor } from '@/lib/language-switch';

type HeaderMenuItem = {
  kind: 'page' | 'category' | 'custom';
  label: string;
  targetSlug?: string;
  url?: string;
  children?: HeaderMenuItem[];
};

type HeaderMenu = {
  menuKey: string;
  name: string;
  items: HeaderMenuItem[];
};

type HeaderCorporateInfo = {
  companyName?: string;
  logoUrl?: string;
  companyLogoUrl?: string;
};

type HeaderProps = {
  corporateInfo?: HeaderCorporateInfo | null;
  categoryMenu?: HeaderMenu | null;
  /** The content of each category-menu root, keyed by the root's slug (resolved by Core; see lib/category-menu-previews.ts). */
  categoryPreviews?: CategoryMenuPreviews;
  headerMainMenu?: HeaderMenu | null;
  texts?: Record<string, string> | null;
  activeLanguage?: string;
  /** Canonical product-listing href resolved by Storefront Core (null when the domain has no
   * listing page). Used only for the built-in fallback menu entries that mean "the product
   * listing"; admin-configured menu URLs are never rewritten. */
  productListingHref?: string | null;
};

function renderCategoryItems(
  items: HeaderMenuItem[],
  activeLanguage: string,
  onNavigate?: () => void,
  level = 0,
): React.ReactNode[] {
  return items.map((item, index) => {
    const key = `${item.label}-${level}-${index}`;
    const hasChildren = Array.isArray(item.children) && item.children.length > 0;

    return (
      <li key={key} className="group relative">
        <Link
          href={menuItemHref(item, activeLanguage)}
          onClick={onNavigate}
          className="flex items-center justify-between gap-3 px-5 py-3 text-[15px] text-dark-2 transition-colors hover:bg-gray hover:text-primary"
        >
          <span>{item.label}</span>
          {hasChildren ? (
            <svg className="h-3.5 w-3.5 text-dark-3 transition-colors group-hover:text-primary" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fillRule="evenodd" d="M7.21 5.23a.75.75 0 011.06-.02l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 11-1.04-1.08l3.94-3.71-3.94-3.71a.75.75 0 01-.02-1.06z" clipRule="evenodd" />
            </svg>
          ) : null}
        </Link>

        {hasChildren ? (
          <div className="invisible absolute left-full top-0 z-50 ml-1 min-w-[240px] rounded-2xl border border-gray-3 bg-white opacity-0 shadow-2 transition-all duration-150 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100">
            <ul>{renderCategoryItems(item.children!, activeLanguage, onNavigate, level + 1)}</ul>
          </div>
        ) : null}
      </li>
    );
  });
}

export function LegacyHeader({
  corporateInfo,
  categoryMenu,
  categoryPreviews,
  headerMainMenu,
  texts,
  activeLanguage = 'en',
  productListingHref,
}: HeaderProps) {
  const t = createTranslator(texts ?? {});
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileCategoriesCollapsed, setIsMobileCategoriesCollapsed] = useState(true);
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
  const [activeCategorySlug, setActiveCategorySlug] = useState<string | null>(null);
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const [isAccountDropdownOpen, setIsAccountDropdownOpen] = useState(false);
  const categoryDropdownRef = useRef<HTMLDivElement | null>(null);
  const languageDropdownRef = useRef<HTMLDivElement | null>(null);
  const accountDropdownRef = useRef<HTMLDivElement | null>(null);
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { customer, isAuthenticated, isLoading: isAuthLoading, logout } = useCustomerAuthContext();

  useEffect(() => {
    if (isMobileMenuOpen) {
      setIsMobileCategoriesCollapsed(true);
    }
  }, [isMobileMenuOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        languageDropdownRef.current &&
        !languageDropdownRef.current.contains(event.target as Node)
      ) {
        setIsLanguageDropdownOpen(false);
      }
      if (
        accountDropdownRef.current &&
        !accountDropdownRef.current.contains(event.target as Node)
      ) {
        setIsAccountDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const { pageLocale } = usePageLocaleContext();

  const langPrefix = activeLanguage !== 'en' ? `/${activeLanguage}` : '';
  const logoSrc = corporateInfo?.logoUrl || corporateInfo?.companyLogoUrl;
  const logoAlt = corporateInfo?.companyName || 'Online Marble Expo';

  // Fallback entries whose target is the product listing resolve through Storefront Core and
  // are omitted when the listing page can't be resolved (there is no /shop route).
  const productListingFallbackItem = (label: string): HeaderMenuItem[] =>
    productListingHref ? [{ kind: 'custom', label, url: productListingHref }] : [];

  // Fallbacks used only when the admin has not configured the menu. Every entry must be a real destination (Phase 13): the
  // former /products, /offers and /contact entries had no route behind them and are intentionally gone.
  const categoryFallbackItems: HeaderMenuItem[] = [
    { kind: 'custom', label: 'Browse by Category', url: '/categories' },
    ...productListingFallbackItem('Best Sellers'),
  ];

  const mainFallbackItems: HeaderMenuItem[] = [
    { kind: 'custom', label: 'Popular', url: '/' },
    ...productListingFallbackItem('Shop'),
    { kind: 'custom', label: 'Categories', url: '/categories' },
  ];

  const categoryMenuItems: HeaderMenuItem[] = categoryMenu?.items?.length
    ? categoryMenu.items
    : categoryFallbackItems;

  const mainMenuItems: HeaderMenuItem[] = headerMainMenu?.items?.length
    ? headerMainMenu.items
    : mainFallbackItems;

  const allCategoriesLabel = t('header.allCategories');

  const categoryRootItems = categoryMenuItems.filter(
    (item) => item.kind === 'category' && typeof item.targetSlug === 'string' && item.targetSlug.trim().length > 0,
  );

  // The URL of the same page in another language (logic shared with every theme's switcher: lib/language-switch.ts).
  const buildLanguageHref = (language: string) =>
    buildLanguageHrefFor(language, {
      pathname,
      query: searchParams.toString(),
      hash: typeof window !== 'undefined' ? window.location.hash : '',
      pageLocale,
    });

  const languageOptions = [
    { code: 'en', label: 'EN' },
    { code: 'tr', label: 'TR' },
  ];

  // The selected root (the first one until another is hovered / focused / clicked) and ITS content — already resolved by Core, so a
  // switch is a plain state change: there is nothing to fetch and nothing to wait for. A root whose content could not be resolved
  // (null) shows the "no content" message, exactly as before.
  const firstRootSlug = categoryRootItems[0]?.targetSlug?.trim() ?? null;
  const selectedCategorySlug = activeCategorySlug ?? firstRootSlug;
  const activeCategoryContent = selectedCategorySlug ? (categoryPreviews?.[selectedCategorySlug] ?? null) : null;

  useEffect(() => {
    function handleOutsidePointerDown(event: MouseEvent) {
      if (!isCategoryDropdownOpen) {
        return;
      }

      if (
        categoryDropdownRef.current &&
        !categoryDropdownRef.current.contains(event.target as Node)
      ) {
        setIsCategoryDropdownOpen(false);
      }
    }

    document.addEventListener('mousedown', handleOutsidePointerDown);
    return () => {
      document.removeEventListener('mousedown', handleOutsidePointerDown);
    };
  }, [isCategoryDropdownOpen]);

  return (
    <header className="sticky top-0 z-40 border-b border-gray-3 bg-white">
      <div className="hidden lg:block border-b border-gray-3 bg-dark text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-2 text-xs">
          {/* The former left-hand tagline ("Premium design, trusted support, …") is gone with the "Premium Design" label: the
              header must not expose "Premium Design" in any form, in any theme. The contact details stay, right-aligned. */}
          <div className="flex items-center justify-end gap-4">
            <div className="flex items-center gap-4 text-gray-300">
              <span>{t('header.helpLabel')} +90 555 000 00 00</span>
              <span className="hidden xl:inline">support@onlinemarbleexpo.com</span>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-[1fr_auto] items-center gap-3 py-3">
          <Link href="/" className="inline-flex items-center gap-2">
            {logoSrc ? (
              <img src={logoSrc} alt={logoAlt} className="h-[50px] w-[165px] object-contain" />
            ) : (
              <>
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-sm font-bold text-white">
                  OM
                </span>
                <span className="text-base sm:text-lg font-semibold text-dark">Online Marble Expo</span>
              </>
            )}
          </Link>

          {/* Storefront-wide header rule (all themes): no Search Products, Favorites/Wishlist, Cart or "Premium Design" in the
              header. Presentation only — the cart/wishlist state, the cart drawer and the search APIs are untouched.
              (No /wishlist page exists: Phase 13, docs/navigation-decisions.md.) */}
          <div className="flex items-center justify-end gap-1 sm:gap-2">
            <div ref={accountDropdownRef} className="relative hidden sm:block">
              <button
                type="button"
                onClick={() => setIsAccountDropdownOpen((current) => !current)}
                className="inline-flex h-10 w-10 items-center justify-center rounded-md text-dark-3 hover:bg-gray hover:text-primary"
                title={t('header.account')}
                aria-haspopup="menu"
                aria-expanded={isAccountDropdownOpen}
              >
                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M10 2.5a4 4 0 100 8 4 4 0 000-8zM4 15a6 6 0 1112 0v.5a.5.5 0 01-.5.5h-11a.5.5 0 01-.5-.5V15z" clipRule="evenodd" />
                </svg>
              </button>

              {isAccountDropdownOpen && !isAuthLoading && (
                <div className="absolute right-0 z-50 mt-2 w-48 overflow-hidden rounded-lg border border-gray-3 bg-white py-1 shadow-2">
                  {isAuthenticated ? (
                    <>
                      <Link
                        href={`${langPrefix}/my-account`}
                        onClick={() => setIsAccountDropdownOpen(false)}
                        className="block truncate px-3 py-2 text-sm font-semibold text-dark-2 hover:bg-gray"
                      >
                        {customer?.firstName ? `${customer.firstName} ${customer.lastName ?? ''}`.trim() : t('account.signedInAs')}
                      </Link>
                      <button
                        type="button"
                        onClick={async () => {
                          setIsAccountDropdownOpen(false);
                          await logout();
                        }}
                        className="block w-full px-3 py-2 text-left text-sm text-dark-2 hover:bg-gray"
                      >
                        {t('auth.logout')}
                      </button>
                    </>
                  ) : (
                    <>
                      <Link
                        href={`${langPrefix}/login`}
                        onClick={() => setIsAccountDropdownOpen(false)}
                        className="block px-3 py-2 text-sm text-dark-2 hover:bg-gray"
                      >
                        {t('auth.login.title')}
                      </Link>
                      <Link
                        href={`${langPrefix}/register`}
                        onClick={() => setIsAccountDropdownOpen(false)}
                        className="block px-3 py-2 text-sm text-dark-2 hover:bg-gray"
                      >
                        {t('auth.registerLink')}
                      </Link>
                    </>
                  )}
                </div>
              )}
            </div>

            <div ref={languageDropdownRef} className="relative">
              <button
                type="button"
                onClick={() => setIsLanguageDropdownOpen((current) => !current)}
                className="inline-flex h-10 items-center gap-1 rounded-md px-2 text-sm font-semibold text-dark-3 hover:bg-gray hover:text-primary"
                aria-haspopup="menu"
                aria-expanded={isLanguageDropdownOpen}
              >
                <span>{activeLanguage.toUpperCase()}</span>
                <svg className={`h-4 w-4 transition-transform ${isLanguageDropdownOpen ? 'rotate-180' : ''}`} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                </svg>
              </button>

              {isLanguageDropdownOpen && (
                <div className="absolute right-0 z-50 mt-2 w-28 overflow-hidden rounded-lg border border-gray-3 bg-white shadow-2">
                  {languageOptions.map((option) => (
                    <Link
                      key={option.code}
                      href={buildLanguageHref(option.code)}
                      className={`block px-3 py-2 text-sm hover:bg-gray ${activeLanguage === option.code ? 'text-primary' : 'text-dark-2'}`}
                      onClick={() => setIsLanguageDropdownOpen(false)}
                    >
                      {option.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="inline-flex h-10 w-10 items-center justify-center rounded-md text-dark-3 hover:bg-gray lg:hidden" aria-label={t('header.toggleMenu')}>
              <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                {isMobileMenuOpen ? (
                  <path fillRule="evenodd" d="M4.22 4.22a.75.75 0 011.06 0L10 8.94l4.72-4.72a.75.75 0 111.06 1.06L11.06 10l4.72 4.72a.75.75 0 11-1.06 1.06L10 11.06l-4.72 4.72a.75.75 0 11-1.06-1.06L8.94 10 4.22 5.28a.75.75 0 010-1.06z" clipRule="evenodd" />
                ) : (
                  <path fillRule="evenodd" d="M3 5.75A.75.75 0 013.75 5h12.5a.75.75 0 010 1.5H3.75A.75.75 0 013 5.75zM3 10a.75.75 0 01.75-.75h12.5a.75.75 0 010 1.5H3.75A.75.75 0 013 10zm.75 3.25a.75.75 0 000 1.5h12.5a.75.75 0 000-1.5H3.75z" clipRule="evenodd" />
                )}
              </svg>
            </button>
          </div>
        </div>

        <nav className="hidden lg:flex items-center justify-between border-t border-gray-3 py-3" aria-label="header-main-menu">
          <div className="flex items-center gap-8">
            <div ref={categoryDropdownRef} className="relative">
              <button
                type="button"
                onClick={() => {
                  const next = !isCategoryDropdownOpen;
                  setIsCategoryDropdownOpen(next);
                  if (next && firstRootSlug) {
                    setActiveCategorySlug(firstRootSlug);
                  }
                }}
                className="inline-flex min-w-[200px] items-center justify-between gap-2 rounded-md border border-gray-3 px-4 py-2 text-sm font-medium text-dark hover:border-primary hover:text-primary"
                aria-expanded={isCategoryDropdownOpen}
                aria-haspopup="menu"
              >
                <span>{allCategoriesLabel}</span>
                <svg className={`h-3.5 w-3.5 transition-transform ${isCategoryDropdownOpen ? 'rotate-180' : ''}`} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                </svg>
              </button>

              {isCategoryDropdownOpen && (
                <div className="absolute left-0 top-[calc(100%+10px)] z-50 w-[920px] rounded-2xl border border-gray-3 bg-white shadow-2" aria-label="category-menu">
                  <div className="grid grid-cols-[300px_1fr]">
                    <div className="border-r border-gray-3 bg-gray p-4">
                      <div className="space-y-1">
                        {categoryRootItems.map((item, index) => {
                          const slug = item.targetSlug!.trim();
                          const isActive = selectedCategorySlug === slug;
                          return (
                            <button
                              key={`${item.label}-${index}`}
                              type="button"
                              onMouseEnter={() => setActiveCategorySlug(slug)}
                              onFocus={() => setActiveCategorySlug(slug)}
                              onClick={() => setActiveCategorySlug(slug)}
                              className={`flex w-full items-center gap-3 rounded-lg px-2.5 py-2 text-left text-sm transition-colors ${
                                isActive ? 'bg-white text-primary shadow-1' : 'text-dark-2 hover:bg-white hover:text-primary'
                              }`}
                            >
                              {activeCategoryContent?.imageUrl && isActive ? (
                                <img src={activeCategoryContent.imageUrl} alt={item.label} className="h-10 w-10 rounded-md object-cover" />
                              ) : (
                                <div className="h-10 w-10 rounded-md bg-white border border-gray-3" />
                              )}
                              <span className="truncate">{item.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div className="p-4">
                      {activeCategoryContent ? (
                        <div>
                          <div className="mb-4 flex items-center gap-3 border-b border-gray-3 pb-3">
                            {activeCategoryContent.imageUrl ? (
                              <img
                                src={activeCategoryContent.imageUrl}
                                alt={activeCategoryContent.title}
                                className="h-14 w-14 rounded-lg object-cover"
                              />
                            ) : (
                              <div className="h-14 w-14 rounded-lg bg-gray border border-gray-3" />
                            )}
                            <h4 className="text-2xl font-semibold text-dark">{activeCategoryContent.title}</h4>
                          </div>

                          <div className="grid grid-cols-2 gap-3">
                            {activeCategoryContent.cards.map((card) => (
                              <Link
                                key={card.slug}
                                href={`${activeLanguage !== 'en' ? `/${activeLanguage}` : ''}/${activeCategoryContent.kind === 'subcategories' ? 'categories' : 'products'}/${card.slug}`}
                                className="flex items-center gap-3 rounded-xl border border-gray-3 p-3 transition-colors hover:border-primary"
                                onClick={() => setIsCategoryDropdownOpen(false)}
                              >
                                {card.imageUrl ? (
                                  <img src={card.imageUrl} alt={card.title} className="h-12 w-12 rounded-md object-cover" />
                                ) : (
                                  <div className="h-12 w-12 rounded-md bg-gray border border-gray-3" />
                                )}
                                <span className="line-clamp-2 text-sm font-medium text-dark-2">{card.title}</span>
                              </Link>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div className="flex h-full min-h-[280px] items-center justify-center text-sm text-dark-3">
                          {t('header.noContent')}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <ul className="flex items-center gap-8 text-sm font-medium text-dark-2">
              {mainMenuItems.map((item, index) => (
                <li key={`${item.label}-${index}`}>
                  <Link href={menuItemHref(item, activeLanguage)} className="hover:text-primary">
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </nav>

        {isMobileMenuOpen && (
          <div className="border-t border-gray-3 py-4 lg:hidden">
            <div className="mb-4 border-b border-gray-3 pb-4">
                <button
                  type="button"
                  onClick={() => setIsMobileCategoriesCollapsed((current) => !current)}
                  className="mb-2 flex w-full items-center justify-between px-3 text-xs font-semibold uppercase tracking-[0.1em] text-dark-3"
                  aria-expanded={!isMobileCategoriesCollapsed}
                >
                  <span>{t('header.categories')}</span>
                  <svg
                    className={`h-4 w-4 transition-transform ${isMobileCategoriesCollapsed ? '' : 'rotate-180'}`}
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                  </svg>
                </button>
                {!isMobileCategoriesCollapsed && (
                <div className="space-y-1">
                  {categoryRootItems.map((item, index) => (
                    <Link
                      key={`${item.label}-mobile-cat-${index}`}
                      href={menuItemHref(item, activeLanguage)}
                      className="block rounded-md px-3 py-2 text-sm text-dark-2 hover:bg-gray hover:text-primary"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
                )}
            </div>
            <nav className="space-y-1 text-sm font-medium text-dark-2">
              {mainMenuItems.map((item, index) => (
                <Link
                  key={`${item.label}-mobile-${index}`}
                  href={menuItemHref(item, activeLanguage)}
                  className="block rounded-md px-3 py-2 hover:bg-gray"
                >
                  {item.label}
                </Link>
              ))}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
